import React, { useCallback, useEffect, useState } from 'react'

export type MoreItemRefs = {
  inMoreMenu?: boolean | null
  ref: React.RefObject<any>
}

export const useMoreItems = (
  itemRefs: MoreItemRefs[],
  moreWidth: number,
  padding: number,
  width?: number
) => {
  const [viewItems, setViewItems] = useState<MoreItemRefs[]>([])
  const [moreItems, setMoreItems] = useState<MoreItemRefs[]>([])

  const recalculateWidths = useCallback(() => {
    const tempViewItems: MoreItemRefs[] = []
    const tempMoreItems: MoreItemRefs[] = []
    let currWidth = moreWidth + padding
    let isFull = false
    if (width) {
      // Until the items lay out, every offsetWidth reads 0 and they'd all  "fit" — putting the full set in viewItems.
      // Short-circuit until one has measured the rerenderRef/resize effect re-runs this once they do.
      const measurableItems = itemRefs.filter((item) => !item.inMoreMenu)
      const anyMeasured = measurableItems.some(
        (item) => (item.ref.current?.offsetWidth || 0) > 0
      )
      if (measurableItems.length > 0 && !anyMeasured) {
        return
      }

      itemRefs.forEach((item) => {
        const itemWidth = item.ref.current?.offsetWidth || 0
        if (item.inMoreMenu) {
          tempMoreItems.push(item)
        } else if (currWidth + itemWidth + padding > width || isFull) {
          tempMoreItems.push(item)
          isFull = true
        } else {
          tempViewItems.push(item)
          currWidth += itemWidth + padding
        }
      })

      setViewItems(tempViewItems)
      setMoreItems(tempMoreItems)
    }
  }, [width, itemRefs, moreWidth, padding])

  useEffect(() => recalculateWidths(), [recalculateWidths])

  // Callback ref to run so first render overflow can be handled
  const rerenderRef: React.LegacyRef<HTMLDivElement> = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        recalculateWidths()
      }
    },
    [recalculateWidths]
  )

  return {
    viewItems,
    moreItems,
    rerenderRef
  }
}

export default useMoreItems
